import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";
import {
  PRODUCT,
  PROFILE_PATH,
  REGISTRATION_PATH,
} from "@/components/constants";
import { ProductResult } from "./sanity-api/types";

/**
 * Custom classnames utility function that merges classnames with tailwindcss classes
 * Classnames that use the same tailwind prefix (e.g. text-*, bg-*) override one another when using the extend object.
 * To allow multiple classes with the same prefix, use the override object.
 * For more info view:
 *  https://github.com/dcastil/tailwind-merge/blob/v2.3.0/docs/recipes.md#adding-custom-scale-from-tailwind-config-to-tailwind-merge-config
 */
const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // Add your custom size utilities to the existing group
      "font-size": [
        "text-h1",
        "text-h1-xl",
        "text-h2",
        "text-h3",
        "text-h4",
        "text-h5",
        "text-h6",
        "text-p",
        "text-subtitle",
        "text-5.5xl",
        "text-xs",
        "text-sm",
      ],
    },
  },
  override: {
    theme: {
      colors: [
        "background",
        "foreground",
        "card",
        "popover",
        "primary",
        "primary-70",
        "secondary",
        "muted",
        "accent",
        "destructive",
        "disabled",
        "border",
        "input",
        "ring",
        "chart",
        "text",
        "subtext",
        "success",
        "warning",
        "error",
        "blue",
        "black",
        "brown",
        "text-black",
        "neutral-lightest",
        "lightest-blue",
        "light-blue",
        "dark-blue",
        "all-loyalty-blue",
        "all-blue",
        "grey-light",
        "grey-medium",
      ],
      gradientColorStops: ["from-secondary", "to-primary"],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return customTwMerge(clsx(inputs));
}

export const handleFetchResponse = async <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  OkResponseType extends string | number | null | object | Array<any>,
>(
  response: Response,
  errorMessage: string = "Fetch - server responded with error",
) => {
  if (!response.ok) {
    const errResponse = await (response.headers
      .get("content-type")
      ?.includes("application/json")
      ? response.json()
      : response.text());

    const cause = {
      error: errResponse,
      url: response.url,
      status: response.status,
    };

    console.error(errorMessage, cause);
    throw new Error(errorMessage, {
      cause,
    });
  }

  return (await response.json()) as OkResponseType;
};

/*
 * Quick portable version of https://github.com/nodejs/node/blob/main/lib/internal/assert/utils.js#L154
 * Cloudflare pages/workers runs on a Node Edge runtime that doesnt include assert/ok
 */
export function assert(
  value: unknown,
  message: string = "AssertionError",
): asserts value {
  if (isNaN(<number>value) && !value) {
    throw new Error(message, {
      cause: {
        actual: value,
        expected: true,
      },
    });
  }
}

export const getRedirectInfo = (pathname: string) => {
  let url = new URL(
    PROFILE_PATH,
    process.env.NEXT_PUBLIC_ACCOR_PLUS_URL,
  ).toString();
  let label = "button.loggedIn";

  if (pathname.includes(`${PRODUCT}/`)) {
    url = new URL(
      REGISTRATION_PATH,
      process.env.NEXT_PUBLIC_ACCOR_PLUS_URL,
    ).toString();
    label = "button.default";
  }

  return { url, label };
};

export const gtmEcommerceItems = (
  product: ProductResult,
  price: string,
  currencyCode: string,
  discount = "0.00",
) => [
  {
    item_id: product?.netsuiteProduct?.item_group_id,
    item_name: product?.title,
    item_category: product?.netsuiteProduct?.business_category_id,
    currency: currencyCode,
    discount, // Discount amount - full price minus the discounted price
    index: 0,
    price: price, // The discounted price the user paid
    location_id: product?.netsuiteProduct?.business_category.location_id,
    price_no_tax: product?.netsuiteProduct?.purchase_base_price,
    quantity: 1,
    sanity_product_id: product._id,
    subscription_code: product?.subscriptionPlanCode?.subscriptionCode,
    plan_code: product?.subscriptionPlanCode?.plan,
    is_partner: product?.isPartner ? "true" : "false",
  },
];

export const gtmPriceAndDiscount = (
  price: string,
  discountedPrice?: number,
) => {
  // If discountedPrice is provided and is a valid number >= 0, use it; otherwise, use calculatedPrice.price)
  const gtmPrice =
    Number(discountedPrice) >= 0 ? String(discountedPrice) : price;

  const gtmDiscount = String(Number(price) - Number(gtmPrice));

  return { gtmPrice, gtmDiscount };
};
